import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { 
  X, ScanBarcode, Loader2, Plus, Camera, 
  Flashlight, RotateCcw, Check, AlertCircle, Package, Edit3, Save
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { MealTimeSelector } from '../components/MealTimeSelector';

const API_URL = '';

export function BarcodeScannerModal({
  isOpen,
  onClose,
  onAddFood,
  savedBarcodes = {},
  onSaveBarcode,
  defaultMealTime = 'breakfast',
  mealTimes,
}) {
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [portionSize, setPortionSize] = useState('');
  const [selectedMealTime, setSelectedMealTime] = useState(defaultMealTime);
  const [cameraError, setCameraError] = useState(null);
  const [scannerReady, setScannerReady] = useState(false);
  
  // Manual entry state for unknown products
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [notFoundBarcode, setNotFoundBarcode] = useState('');
  const [manualProduct, setManualProduct] = useState({
    name: '',
    brand: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
  });
  const [saveToMyProducts, setSaveToMyProducts] = useState(true);
  const [editingProductName, setEditingProductName] = useState(false);
  const [editedName, setEditedName] = useState('');
  
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Clean up scanner on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      stopScanner();
      setBarcodeInput('');
      setScannedProduct(null);
      setPortionSize('');
      setCameraError(null);
      setScannerReady(false);
      setShowManualEntry(false);
      setNotFoundBarcode('');
      setManualProduct({ name: '', brand: '', calories: '', protein: '', carbs: '', fat: '' });
    }
  }, [isOpen]);

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.log('Scanner cleanup:', err.message);
      }
      html5QrCodeRef.current = null;
    }
    setIsCameraActive(false);
    setScannerReady(false);
  }, []);

  const startScanner = useCallback(async () => {
    await stopScanner();
    setCameraError(null);
    
    // First show the camera view (which creates the scanner DOM container)
    setIsCameraActive(true);
    
    // Wait for DOM to render the scanner container
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (!scannerRef.current) {
      setCameraError('Scanner container not ready. Please try again.');
      setIsCameraActive(false);
      return;
    }
    
    try {
      const scanner = new Html5Qrcode('barcode-scanner-region', { verbose: false });
      html5QrCodeRef.current = scanner;
      
      const config = {
        fps: 15,
        qrbox: { width: 280, height: 120 },
        aspectRatio: 1.777,
        formatsToSupport: [
          0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15 // All barcode formats
        ]
      };
      
      await scanner.start(
        { facingMode: 'environment' },
        config,
        async (decodedText) => {
          // Vibrate on successful scan
          if (navigator.vibrate) {
            navigator.vibrate(100);
          }
          console.log('Barcode detected:', decodedText);
          await stopScanner();
          lookupBarcode(decodedText);
        },
        () => {} // Ignore scan failures (noise)
      );
      
      setScannerReady(true);
      toast.success('Camera ready! Point at barcode');
      
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setCameraError(
        err.name === 'NotAllowedError' 
          ? 'Camera access denied. Please enable camera permissions.'
          : err.name === 'NotFoundError'
            ? 'No camera found on this device.'
            : `Camera error: ${err.message}`
      );
      setIsCameraActive(false);
    }
  }, [stopScanner]);

  const lookupBarcode = async (barcode) => {
    if (!barcode.trim()) return;
    
    setIsLookingUp(true);
    setBarcodeInput(barcode);
    
    // Check saved barcodes first
    if (savedBarcodes[barcode]) {
      setScannedProduct({
        ...savedBarcodes[barcode],
        barcode,
        fromCache: true,
      });
      setIsLookingUp(false);
      toast.success('Found in your saved products!');
      return;
    }
    
    try {
      // Use backend proxy to avoid CORS issues
      const response = await fetch(`${API_URL}/api/barcode/lookup/${barcode}`);
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.found) {
        const product = {
          barcode: data.barcode,
          name: data.name,
          brand: data.brand,
          image: data.image,
          calories: data.calories,
          protein: data.protein,
          carbs: data.carbs,
          fat: data.fat,
          fiber: data.fiber,
          sugar: data.sugar,
          servingSize: data.serving_size,
          quantity: data.quantity,
        };
        setScannedProduct(product);
        toast.success(`Found: ${product.name}`);
      } else {
        // Product not found - show manual entry form
        setNotFoundBarcode(barcode);
        setShowManualEntry(true);
        toast.info('Product not found. Add it manually!');
      }
    } catch (err) {
      console.error('Barcode lookup error:', err);
      // On error, also allow manual entry
      setNotFoundBarcode(barcode);
      setShowManualEntry(true);
      toast.error('Lookup failed. Add product manually.');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleManualSubmit = () => {
    if (barcodeInput.trim()) {
      lookupBarcode(barcodeInput.trim());
    }
  };

  const handleManualProductAdd = () => {
    if (!manualProduct.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    const portion = parseInt(portionSize) || 0;
    const multiplier = portion / 100;
    
    const calories = parseFloat(manualProduct.calories) || 0;
    const protein = parseFloat(manualProduct.protein) || 0;
    const carbs = parseFloat(manualProduct.carbs) || 0;
    const fat = parseFloat(manualProduct.fat) || 0;

    const foodItem = {
      id: Date.now(),
      name: manualProduct.name.trim(),
      brand: manualProduct.brand.trim(),
      barcode: notFoundBarcode,
      calories: Math.round(calories * multiplier),
      protein: Math.round(protein * multiplier * 10) / 10,
      carbs: Math.round(carbs * multiplier * 10) / 10,
      fat: Math.round(fat * multiplier * 10) / 10,
      grams: portion,
      source: 'barcode-manual',
      mealTime: selectedMealTime,
    };

    onAddFood(foodItem);

    // Save to my products if checked
    if (saveToMyProducts && onSaveBarcode && notFoundBarcode) {
      onSaveBarcode(notFoundBarcode, {
        name: manualProduct.name.trim(),
        brand: manualProduct.brand.trim(),
        calories: calories,
        protein: protein,
        carbs: carbs,
        fat: fat,
      });
      toast.success(`Added and saved "${manualProduct.name}" to your products!`);
    } else {
      toast.success(`Added ${portion}g of ${manualProduct.name}`);
    }

    handleClose();
  };

  const handleAddFood = () => {
    if (!scannedProduct) return;
    
    const portion = parseInt(portionSize) || 0;
    const multiplier = portion / 100;
    
    const foodItem = {
      id: Date.now(),
      name: scannedProduct.name,
      brand: scannedProduct.brand,
      barcode: scannedProduct.barcode,
      calories: Math.round(scannedProduct.calories * multiplier),
      protein: Math.round(scannedProduct.protein * multiplier * 10) / 10,
      carbs: Math.round(scannedProduct.carbs * multiplier * 10) / 10,
      fat: Math.round(scannedProduct.fat * multiplier * 10) / 10,
      grams: portion,
      source: 'barcode',
      mealTime: selectedMealTime,
    };
    
    onAddFood(foodItem);
    
    // Save barcode for future use
    if (!scannedProduct.fromCache && onSaveBarcode) {
      onSaveBarcode(scannedProduct.barcode, {
        name: scannedProduct.name,
        brand: scannedProduct.brand,
        image: scannedProduct.image,
        calories: scannedProduct.calories,
        protein: scannedProduct.protein,
        carbs: scannedProduct.carbs,
        fat: scannedProduct.fat,
      });
    }
    
    toast.success(`Added ${portion}g of ${scannedProduct.name}`);
    handleClose();
  };

  const handleClose = () => {
    stopScanner();
    setBarcodeInput('');
    setScannedProduct(null);
    setPortionSize('100');
    setCameraError(null);
    setShowManualEntry(false);
    setNotFoundBarcode('');
    setManualProduct({ name: '', brand: '', calories: '', protein: '', carbs: '', fat: '' });
    setEditingProductName(false);
    setEditedName('');
    onClose();
  };

  const handleScanAnother = () => {
    setScannedProduct(null);
    setBarcodeInput('');
    setPortionSize('100');
    setShowManualEntry(false);
    setNotFoundBarcode('');
    setManualProduct({ name: '', brand: '', calories: '', protein: '', carbs: '', fat: '' });
    setEditingProductName(false);
    setEditedName('');
  };

  if (!isOpen) return null;

  // Product result view
  if (scannedProduct) {
    const portion = parseInt(portionSize) || 0;
    const multiplier = portion / 100;
    
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="h-5 w-5 text-green-400" />
              Product Found
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Product card */}
            <div className="flex gap-4 p-4 bg-slate-800/80 rounded-xl border border-slate-700">
              {scannedProduct.image ? (
                <img
                  src={scannedProduct.image}
                  alt={scannedProduct.name}
                  className="w-20 h-20 object-contain rounded-lg bg-white p-1"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-20 h-20 bg-slate-700 rounded-lg flex items-center justify-center">
                  <Package className="h-8 w-8 text-slate-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {editingProductName ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white text-sm h-8"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && editedName.trim()) {
                          setScannedProduct(p => ({ ...p, name: editedName.trim() }));
                          setEditingProductName(false);
                        }
                      }}
                    />
                    <button onClick={() => {
                      if (editedName.trim()) {
                        setScannedProduct(p => ({ ...p, name: editedName.trim() }));
                      }
                      setEditingProductName(false);
                    }} className="text-green-400 hover:text-green-300 p-1" data-testid="save-product-name-btn">
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <h3 className="font-semibold text-white truncate">{scannedProduct.name}</h3>
                    <button onClick={() => { setEditedName(scannedProduct.name); setEditingProductName(true); }}
                      className="text-amber-400 hover:text-amber-300 p-1"
                      data-testid="edit-product-name-btn">
                      <Edit3 className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {scannedProduct.brand && (
                  <p className="text-slate-400 text-sm truncate">{scannedProduct.brand}</p>
                )}
                <p className="text-slate-500 text-xs font-mono mt-1">{scannedProduct.barcode}</p>
                {scannedProduct.fromCache && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-400 mt-1">
                    <Check className="h-3 w-3" />
                    Saved product
                  </span>
                )}
              </div>
            </div>

            {/* Portion size input */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400 font-medium">Portion size (grams)</label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={portionSize}
                  onChange={(e) => setPortionSize(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white text-lg font-semibold w-24"
                  placeholder="g"
                  min="1"
                  max="2000"
                />
                <span className="text-slate-400">g</span>
                <div className="flex gap-1 ml-auto">
                  {[50, 100, 150, 200].map(size => (
                    <button
                      key={size}
                      onClick={() => setPortionSize(size.toString())}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        parseInt(portionSize) === size
                          ? 'bg-indigo-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Nutrition preview */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-xl p-4 border border-slate-700">
              <p className="text-sm text-slate-400 mb-3">Nutrition{portion > 0 ? ` for ${portion}g` : ' per 100g'}:</p>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    {Math.round(scannedProduct.calories * multiplier)}
                  </div>
                  <div className="text-xs text-slate-500">kcal</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-red-400">
                    {Math.round(scannedProduct.protein * multiplier * 10) / 10}g
                  </div>
                  <div className="text-xs text-slate-500">Protein</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-yellow-400">
                    {Math.round(scannedProduct.carbs * multiplier * 10) / 10}g
                  </div>
                  <div className="text-xs text-slate-500">Carbs</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-400">
                    {Math.round(scannedProduct.fat * multiplier * 10) / 10}g
                  </div>
                  <div className="text-xs text-slate-500">Fat</div>
                </div>
              </div>
            </div>

            {/* Meal time selector */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400 font-medium">Add to meal:</label>
              <MealTimeSelector 
                selectedMealTime={selectedMealTime} 
                onMealTimeChange={setSelectedMealTime}
                compact
                mealTimes={mealTimes}
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleAddFood}
                className="flex-1 bg-green-600 hover:bg-green-500 h-12 text-base font-semibold"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add to Today
              </Button>
              <Button
                onClick={handleScanAnother}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 h-12"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Scan Again
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Manual entry view for unknown products
  if (showManualEntry) {
    const portion = parseInt(portionSize) || 0;
    const multiplier = portion / 100;
    const calories = parseFloat(manualProduct.calories) || 0;
    const protein = parseFloat(manualProduct.protein) || 0;
    const carbs = parseFloat(manualProduct.carbs) || 0;
    const fat = parseFloat(manualProduct.fat) || 0;

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-amber-400" />
              Add New Product
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info banner */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <p className="text-amber-400 text-sm">
                Product not found for barcode: <span className="font-mono">{notFoundBarcode}</span>
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Enter the nutrition info manually (per 100g)
              </p>
            </div>

            {/* Product name */}
            <div className="space-y-2">
              <Label className="text-slate-300">Product Name *</Label>
              <Input
                value={manualProduct.name}
                onChange={(e) => setManualProduct(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Chocolate Bar"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>

            {/* Brand */}
            <div className="space-y-2">
              <Label className="text-slate-300">Brand (optional)</Label>
              <Input
                value={manualProduct.brand}
                onChange={(e) => setManualProduct(p => ({ ...p, brand: e.target.value }))}
                placeholder="e.g., Nestlé"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>

            {/* Macros grid */}
            <div className="space-y-2">
              <Label className="text-slate-300">Nutrition per 100g</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-orange-400 mb-1 block">Calories (kcal)</label>
                  <Input
                    type="number"
                    value={manualProduct.calories}
                    onChange={(e) => setManualProduct(p => ({ ...p, calories: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-800 border-slate-600 text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="text-xs text-red-400 mb-1 block">Protein (g)</label>
                  <Input
                    type="number"
                    value={manualProduct.protein}
                    onChange={(e) => setManualProduct(p => ({ ...p, protein: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-800 border-slate-600 text-white"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="text-xs text-yellow-400 mb-1 block">Carbs (g)</label>
                  <Input
                    type="number"
                    value={manualProduct.carbs}
                    onChange={(e) => setManualProduct(p => ({ ...p, carbs: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-800 border-slate-600 text-white"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="text-xs text-blue-400 mb-1 block">Fat (g)</label>
                  <Input
                    type="number"
                    value={manualProduct.fat}
                    onChange={(e) => setManualProduct(p => ({ ...p, fat: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-800 border-slate-600 text-white"
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>
            </div>

            {/* Portion size */}
            <div className="space-y-2">
              <Label className="text-slate-300">Portion size (grams)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={portionSize}
                  onChange={(e) => setPortionSize(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white text-lg font-semibold w-24"
                  placeholder="g"
                  min="1"
                  max="2000"
                />
                <span className="text-slate-400">g</span>
                <div className="flex gap-1 ml-auto">
                  {[50, 100, 150].map(size => (
                    <button
                      key={size}
                      onClick={() => setPortionSize(size.toString())}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        parseInt(portionSize) === size
                          ? 'bg-amber-500 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            {(calories > 0 || protein > 0 || carbs > 0 || fat > 0) && (
              <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700">
                <p className="text-xs text-slate-400 mb-2">Preview{portionSize ? ` for ${portionSize}g` : ' per 100g'}:</p>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <div className="text-lg font-bold text-orange-400">{Math.round(calories * multiplier)}</div>
                    <div className="text-xs text-slate-500">kcal</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-red-400">{Math.round(protein * multiplier * 10) / 10}g</div>
                    <div className="text-xs text-slate-500">Protein</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-yellow-400">{Math.round(carbs * multiplier * 10) / 10}g</div>
                    <div className="text-xs text-slate-500">Carbs</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-blue-400">{Math.round(fat * multiplier * 10) / 10}g</div>
                    <div className="text-xs text-slate-500">Fat</div>
                  </div>
                </div>
              </div>
            )}

            {/* Meal time selector */}
            <div className="space-y-2">
              <Label className="text-slate-300">Add to meal:</Label>
              <MealTimeSelector 
                selectedMealTime={selectedMealTime} 
                onMealTimeChange={setSelectedMealTime}
                compact
                mealTimes={mealTimes}
              />
            </div>

            {/* Save to my products checkbox */}
            <label className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl cursor-pointer hover:bg-slate-800 transition-colors">
              <input
                type="checkbox"
                checked={saveToMyProducts}
                onChange={(e) => setSaveToMyProducts(e.target.checked)}
                className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
              />
              <div>
                <span className="text-white text-sm font-medium">Save to My Products</span>
                <p className="text-slate-400 text-xs">Quick access next time you scan this barcode</p>
              </div>
            </label>

            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleManualProductAdd}
                disabled={!manualProduct.name.trim()}
                className="flex-1 bg-amber-600 hover:bg-amber-500 h-12 text-base font-semibold disabled:opacity-50"
              >
                <Save className="h-5 w-5 mr-2" />
                Add Product
              </Button>
              <Button
                onClick={handleScanAnother}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 h-12"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Camera scanner view (contained dialog, not fullscreen)
  if (isCameraActive) {
    return (
      <Dialog open={isOpen} onOpenChange={() => { stopScanner(); onClose(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-[calc(100vw-2rem)] sm:max-w-md p-0 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <ScanBarcode className="h-5 w-5 text-indigo-400" />
              Scan Barcode
            </h2>
          </div>

          {/* Camera area */}
          <div className="relative mx-4 rounded-xl overflow-hidden bg-black" style={{ height: '280px' }} ref={scannerRef}>
            <div 
              id="barcode-scanner-region"
              className="w-full h-full"
              style={{ minHeight: '280px' }}
            />
            
            {/* Scanner overlay with guides */}
            {scannerReady && (
              <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
                <div className="absolute inset-0 bg-black/30" />
                <div className="relative w-64 h-24 z-10">
                  <div className="absolute inset-0 border-2 border-indigo-400 rounded-lg bg-transparent" 
                       style={{ boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)' }}>
                    <div className="absolute -top-1 -left-1 w-6 h-6 border-t-3 border-l-3 border-indigo-400 rounded-tl-lg" />
                    <div className="absolute -top-1 -right-1 w-6 h-6 border-t-3 border-r-3 border-indigo-400 rounded-tr-lg" />
                    <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-3 border-l-3 border-indigo-400 rounded-bl-lg" />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-3 border-r-3 border-indigo-400 rounded-br-lg" />
                    <div className="absolute inset-x-3 h-0.5 bg-gradient-to-r from-transparent via-indigo-400 to-transparent animate-pulse top-1/2 -translate-y-1/2" />
                  </div>
                </div>
                <p className="text-white text-xs mt-3 z-10 drop-shadow-lg">
                  Position barcode in the frame
                </p>
              </div>
            )}
            
            {/* Camera loading state */}
            {!scannerReady && !cameraError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <div className="text-center">
                  <Loader2 className="h-10 w-10 animate-spin text-indigo-400 mx-auto mb-2" />
                  <p className="text-white text-sm">Starting camera...</p>
                  <p className="text-slate-400 text-xs mt-1">Please allow camera access</p>
                </div>
              </div>
            )}
            
            {/* Camera error */}
            {cameraError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black p-4">
                <div className="text-center">
                  <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-2" />
                  <h3 className="text-white font-semibold mb-1">Camera Error</h3>
                  <p className="text-slate-400 text-xs mb-3">{cameraError}</p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={startScanner} size="sm" className="bg-indigo-600 hover:bg-indigo-500">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry
                    </Button>
                    <Button onClick={() => { stopScanner(); setShowManualEntry(true); }} size="sm" variant="outline" className="border-slate-600 text-slate-300">
                      Enter Manually
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom controls */}
          <div className="px-4 pb-4 pt-3">
            <Button
              onClick={stopScanner}
              variant="outline"
              className="w-full h-10 border-slate-600 text-white font-medium hover:bg-white/10"
            >
              <X className="h-4 w-4 mr-2" />
              Close Camera
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Scanner view (camera not active)
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-[calc(100vw-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ScanBarcode className="h-5 w-5 text-indigo-400" />
            Scan Barcode
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Start camera button */}
          <Button
            onClick={startScanner}
            className="w-full h-14 bg-indigo-600 hover:bg-indigo-500 text-base font-semibold"
          >
            <Camera className="h-6 w-6 mr-3" />
            Open Camera Scanner
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-slate-900 text-slate-500">or enter manually</span>
            </div>
          </div>

          {/* Manual barcode input */}
          <div className="flex gap-2">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="Enter barcode number..."
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value.replace(/\D/g, ''))}
              className="bg-slate-800 border-slate-600 text-white text-lg font-mono"
              onKeyPress={(e) => e.key === 'Enter' && handleManualSubmit()}
            />
            <Button
              onClick={handleManualSubmit}
              disabled={isLookingUp || !barcodeInput.trim()}
              className="bg-indigo-600 hover:bg-indigo-500 px-6"
            >
              {isLookingUp ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Search'
              )}
            </Button>
          </div>

          {/* Tip */}
          <p className="text-xs text-slate-500 text-center">
            Supports EAN-13, UPC-A, and most barcode formats
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BarcodeScannerModal;
